import { useCallback, useMemo } from 'react';
import type { LocationPermissionResponse } from 'expo-location';

import {
  type PermissionsMachineImplementations,
  useInitPermissionsMachine,
  usePermissionsMachine,
} from '@sg/shared-fe-features-permissions';

import { useTelemetry } from '@order/Telemetry';

import { useLocationPermissionPrimerEnabled } from '../useLocationPermissionPrimerEnabled';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Drives the location permission primer flow shown on app open.
 *
 * Returns the flags + handlers the consumer needs to decide whether to render
 * a loading screen, the primer, or its normal content.
 */
export const useLocationPermissionPrimer = () => {
  const { track } = useTelemetry();
  const { canEnableLocationPrimer: isLocationPrimerEnabled } = useLocationPermissionPrimerEnabled();

  // ─── Actions ─────────────────────────────────────────────────────────

  const onLocationPrimerShow = useCallback(() => {
    track('location_primer_shown', LOCATION_PRIMER_TELEMETRY_PAYLOAD);
  }, [track]);

  const onRequestLocationPermission = useCallback(() => {
    track('location_primer_cta_tapped', LOCATION_PRIMER_TELEMETRY_PAYLOAD);
  }, [track]);

  const onSkipLocationPermission = useCallback(() => {
    track('location_primer_dismissed', {
      ...LOCATION_PRIMER_TELEMETRY_PAYLOAD,
      action: 'not_now',
    });
  }, [track]);

  const onLocationPermissionGranted = useCallback(
    (_args: unknown, _permissionResponse: LocationPermissionResponse) => {
      track('location_permission_granted', LOCATION_PRIMER_TELEMETRY_PAYLOAD);
    },
    [track],
  );

  const onLocationPermissionDenied = useCallback(
    (_args: unknown, _permissionResponse?: LocationPermissionResponse) => {
      track('location_permission_denied', LOCATION_PRIMER_TELEMETRY_PAYLOAD);
    },
    [track],
  );

  const onAllowOnceDetected = useCallback(() => {
    track('location_allow_once_detected');
  }, [track]);

  // ─────────────────────────────────────────────────────────────────────

  const implementations = useMemo<PermissionsMachineImplementations>(
    () => ({
      actions: {
        onLocationPrimerShow,
        onRequestLocationPermission,
        onSkipLocationPermission,
        onLocationPermissionGranted,
        onLocationPermissionDenied,
        onAllowOnceDetected,
      },
    }),
    [
      onAllowOnceDetected,
      onLocationPermissionDenied,
      onLocationPermissionGranted,
      onLocationPrimerShow,
      onRequestLocationPermission,
      onSkipLocationPermission,
    ],
  );

  // ─── Actor ───────────────────────────────────────────────────────────

  const permissionsActorRef = useInitPermissionsMachine({
    isLocationPrimerEnabled,
    implementations,
  });

  const {
    isLocationPermissionsLoading,
    isLocationPrimerVisible,
    isRequestingLocationPermission,
    isSkippingLocationPermission,
    isLocationPermissionDenied,
    isLocationPermissionSkipped,
    isLocationPermissionAlreadyShown,
    requestLocationPermissions,
    skipLocationPermissions,
  } = usePermissionsMachine(permissionsActorRef);

  // ─── Derived ─────────────────────────────────────────────────────────

  const isPrimerActive =
    isLocationPrimerVisible || isRequestingLocationPermission || isSkippingLocationPermission;

  // ─────────────────────────────────────────────────────────────────────

  return {
    isLocationPrimerEnabled,
    isLocationPermissionsLoading,
    isPrimerActive,
    isRequestingLocationPermission,
    isSkippingLocationPermission,
    isLocationPermissionDenied,
    isLocationPermissionSkipped,
    isLocationPermissionAlreadyShown,
    onAllow: requestLocationPermissions,
    onSkip: skipLocationPermissions,
  };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const LOCATION_PRIMER_TELEMETRY_PAYLOAD = { entry_point: 'app-open' };
